import { Link } from "react-router-dom";

// "Dial. Lock. Dispense. Store.", 4-step annotated pen kit walkthrough.
// Uses the horizontal generic TrentaMedical pen asset rotated -90deg so
// the pen reads top-to-bottom in the frame. Callout dots are anchored by
// pixel-percentage coordinates to specific features on the actual pen
// image (dose counter, blue lock ring, mid-body dispense zone, cap/tip).
//
// Layout: the frame's aspect is intentionally tall (2:3) so the pen
// occupies ~90% of the vertical space, eliminating the "small object in
// a big card" look. Pre-rotation image width is set explicitly so that
// after -90deg rotation the visual bounds stay inside the overflow-hidden
// frame, CSS transforms don't change layout so height-based sizing was
// causing clipping (see iteration_28 report).
const STEPS = [
  {
    n: "01",
    label: "DIAL",
    body: "Set the dial to the required dispensing setting. The mechanical system provides consistent, repeatable adjustment.",
    // Feature = dose counter window at the very top of the vertical pen.
    // Pen occupies ~10-90% of frame height; dial is at top-most ~7% of pen.
    top: "15%",
    side: "right",
  },
  {
    n: "02",
    label: "LOCK",
    body: "An audible click confirms the selected setting is locked.",
    // Feature = blue click/lock ring just below the dial (~14% along pen).
    top: "22%",
    side: "left",
  },
  {
    n: "03",
    label: "DISPENSE",
    body: "Press the actuator to dispense the selected volume.",
    // Feature = mid-body dispense zone (~50% along pen).
    top: "50%",
    side: "right",
  },
  {
    n: "04",
    label: "STORE",
    body: "Replace the cap and return the pen to the specified 2–8°C storage environment. Total usage duration depends on the selected setting and frequency of use.",
    // Feature = cap/tip end at the bottom (~90% along pen).
    top: "82%",
    side: "left",
  },
];

const PEN_SRC = "/product-photos/generic-pen/pen.webp";

// Post-rotation pen edges as a % of frame width. The pen is centered
// horizontally and, at min(120%,640px) pre-rotation width inside a 440px
// frame, its post-rotation visual width lands at ~40% of the frame,
// giving edges at ~30% and ~70%.
const DOT_LEFT_EDGE = "31%";
const DOT_RIGHT_EDGE = "69%";

export default function PenAnnotation() {
  return (
    <section className="border-b border-line bg-white" data-testid="pen-annotation">
      <div className="container-pd py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="label-mono text-brandblue">§ Pen kit workflow</p>
            <h2 className="mt-3 font-heading text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Dial. Lock.
              <br />
              <span className="accent-italic">Dispense. Store.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-subtle">
              Pen kits eliminate the manual reconstitution step. Pre-filled,
              pre-metered and designed for controlled dispensing in a
              temperature-controlled laboratory environment.
            </p>
            <div className="mt-8 space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-4" data-testid={`pen-step-${s.n}`}>
                  <span className="font-mono text-2xl font-black text-brandblue/40">{s.n}</span>
                  <div>
                    <p className="font-heading text-lg font-black text-ink">{s.label}</p>
                    <p className="mt-1 text-sm leading-relaxed text-subtle">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/pen-kits"
              className="mt-10 inline-flex btn-pill bg-brandblue px-6 py-3 text-sm font-bold text-white hover:bg-brandbluedark"
              data-testid="pen-annotation-cta"
            >
              Shop Pen Kits →
            </Link>
          </div>

          <div className="relative lg:col-span-7">
            {/* Tall frame (2:3) so the rotated pen dominates. bg-white
                matches the pen photo's background, no seam. */}
            <div
              className="relative mx-auto flex aspect-[2/3] w-full max-w-[440px] items-center justify-center overflow-hidden rounded-2xl panel-warm bg-white"
              data-testid="pen-frame"
            >
              {/* Pen sized by pre-rotation width so post-rotation bounds
                  are known. min(120%,640px) → at 440 frame gives 528px
                  pre-rot width, aspect 3:1 gives ~176px pre-rot height,
                  post-rot bounds ≈ 176 x 528 (fits in 440x660 frame,
                  occupying ~80% of the vertical space). */}
              <img
                src={PEN_SRC}
                alt="Trenta Medical pen kit"
                className="block max-w-none origin-center object-contain"
                style={{
                  width: "min(120%, 640px)",
                  height: "auto",
                  transform: "rotate(-90deg)",
                }}
                draggable={false}
                data-testid="pen-annotation-photo"
              />

              {/* Callouts. Each renders a label chip near the frame edge
                  plus a dot ON the pen surface, connected by a leader
                  line. Positioned by percentage so they track the pen
                  across breakpoints (frame aspect is fixed). */}
              {STEPS.map((s) => (
                <Callout key={s.n} label={s.label} top={s.top} side={s.side} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Callout({ label, top, side }) {
  const isRight = side === "right";
  // Row spans from the pen edge (where the dot lives) to the frame edge
  // (where the label lives). Absolutely positioned by percentages so the
  // dot lands on the pen surface regardless of viewport.
  const styleRow = isRight
    ? { top, left: DOT_RIGHT_EDGE, right: "0.75rem", transform: "translateY(-50%)" }
    : { top, left: "0.75rem", right: `calc(100% - ${DOT_LEFT_EDGE})`, transform: "translateY(-50%)" };
  return (
    <div
      className="pointer-events-none absolute z-10 hidden items-center gap-2 md:flex"
      style={styleRow}
      data-testid={`pen-callout-${label.toLowerCase()}`}
    >
      {isRight ? (
        <>
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-brandblue ring-2 ring-white"
            aria-hidden="true"
          />
          <span className="h-px flex-1 bg-brandblue/40" aria-hidden="true" />
          <span className="rounded-full bg-brandblue/10 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.18em] text-brandblue">
            {label}
          </span>
        </>
      ) : (
        <>
          <span className="rounded-full bg-brandblue/10 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.18em] text-brandblue">
            {label}
          </span>
          <span className="h-px flex-1 bg-brandblue/40" aria-hidden="true" />
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-brandblue ring-2 ring-white"
            aria-hidden="true"
          />
        </>
      )}
    </div>
  );
}
